/* eslint-disable react-refresh/only-export-components */
import { useState, type ReactNode } from 'react';
import { useListPanelSearch } from '@/hooks/lib/useListPanelState';
import { endOfDay, format, parseISO } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { ArrowDown, ArrowUp, Package, RefreshCw } from 'lucide-react';
import { usePricebookItems } from '@/hooks/pricebook/usePricebookItems';
import { useDynamicPricePreviews, useLaborRateProfiles } from '@/hooks/pricebook/useDynamicPricing';
import { pricebookListCost } from '@/components/organisms/pricebook/support/pricebook-list-cost';
import { useTags } from '@/hooks/tags/useTags';
import { useBusinessUnits } from '@/hooks/scheduler/useBusinessUnits';
import { useCategoryList } from '@/hooks/pricebook/usePricebookCategories';
import { buildCategoryPaths } from '@/components/organisms/pricebook/support/category-paths';
import type { LaborRateProfile, PricebookItem } from '@/types/pricebook';
import { useHasAnyPermission, useIsFieldHidden } from '@/hooks/usePermissions';
import {
  PRICEBOOK_ARCHIVED_FILTER_PERMISSIONS,
  pricebookListParamsForTypeFilter,
  pricebookTypeFilterOptions,
  type PricebookTypeFilter,
} from '@/components/organisms/pricebook/support/type-filter';

export {
  PRICEBOOK_ARCHIVED_FILTER_PERMISSIONS,
  PRICEBOOK_TYPE_FILTER_VALUES,
  pricebookListParamsForTypeFilter,
  type PricebookTypeFilter,
} from '@/components/organisms/pricebook/support/type-filter';
import {
  fmt,
  isTimeAndMaterialsPricebookItem,
  pricebookPrimaryPrice,
  pricebookPrimaryPriceSuffix,
} from '@/components/organisms/pricebook/support/pricebook-helpers';
import { EntityListCompactItem, EntitySplitListPanel } from '@/components/organisms';
import { DataTableFilterSection } from '@/components/organisms/DataTableFilterSection';
import { ItemTypeBadge } from '@/components/organisms/pricebook/ItemTypeBadge';
import { PricebookItemActions } from '@/components/organisms/pricebook/PricebookItemActions';
import {
  DataTableDateRangeField,
  DataTableNumberFilterField,
  DataTableSearchFilterField,
  DataTableSelectFilterField,
  DataTableTextFilterField,
  EntityListActiveDatePill,
  EntityListAdvancedFiltersSheet,
  EntityListCountFooter,
  EntityRowActions,
  type DataTableNumberOperator,
} from '@/components/molecules';

export interface PricebookListFilters {
  q: string;
  type: PricebookTypeFilter;
  sku: string;
  priceOp: DataTableNumberOperator;
  priceValue: string;
  costOp: DataTableNumberOperator;
  costValue: string;
  priceType: 'all' | 'flat' | 'hourly';
  /** Business Unit id text match. Named honestly — pricebook Category is `categoryId`. */
  businessUnit: string;
  /** Pricebook browse folder; applied SERVER-side, subfolders included. */
  categoryId: string;
  unit: string;
  taxable: 'all' | 'yes' | 'no';
  glAccount: string;
  sort: number;
  dateFrom: string | undefined;
  dateTo: string | undefined;
  updatedFrom: string | undefined;
  updatedTo: string | undefined;
  tagIds: string[];
}

interface PricebookListPanelProps {
  selectedId?: string;
  onSelect: (item: PricebookItem) => void;
  filters: PricebookListFilters;
  onFiltersChange: (patch: Partial<PricebookListFilters>) => void;
}

type SortOption = {
  label: string;
  sortBy: 'createdAt' | 'updatedAt' | 'name' | 'price';
  sortDir: 'asc' | 'desc';
  icon: ReactNode;
};

export const DEFAULT_PRICEBOOK_LIST_FILTERS: PricebookListFilters = {
  q: '',
  type: 'all',
  sku: '',
  priceOp: 'eq',
  priceValue: '',
  costOp: 'eq',
  costValue: '',
  priceType: 'all',
  businessUnit: '',
  categoryId: '',
  unit: '',
  taxable: 'all',
  glAccount: '',
  sort: 0,
  dateFrom: undefined,
  dateTo: undefined,
  updatedFrom: undefined,
  updatedTo: undefined,
  tagIds: [],
};

/**
 * A FILTER THE USER CANNOT HAVE IS READ AS ITS DEFAULT.
 *
 * The whole filter set travels in the URL, and the person who opens a shared or
 * bookmarked link is not always the person who saved it. A filter they have no
 * access to must not silently decide what they see — it is simply not applied.
 *
 * Reading it as the default reveals strictly nothing: every row is listed
 * whatever it costs, so there is no surviving set to infer a cost from. The
 * panel still answers `undefined` for a masked row's cost — that is the
 * defence; this is the URL not asking.
 */
export function pricebookFiltersForAccess(
  filters: PricebookListFilters,
  access: { canSeeArchived: boolean; profitabilityHidden: boolean },
): PricebookListFilters {
  let next = filters;
  // `?type=archived` must not turn into a 403 for a user the pill is hidden
  // from: read it as the default view instead.
  if (filters.type === 'archived' && !access.canSeeArchived) {
    next = { ...next, type: DEFAULT_PRICEBOOK_LIST_FILTERS.type };
  }
  // `?costValue=` likewise, with PROFITABILITY masked. Cost is UNKNOWN to such
  // a user, so the filter matches nothing and the list empties — while the Cost
  // control that would clear it is (correctly) not rendered, leaving the filter
  // sheet counting something it does not display. Only a blanket Reset got them
  // out, and it took every other filter with it.
  if (
    access.profitabilityHidden &&
    (filters.costValue || filters.costOp !== DEFAULT_PRICEBOOK_LIST_FILTERS.costOp)
  ) {
    next = {
      ...next,
      costOp: DEFAULT_PRICEBOOK_LIST_FILTERS.costOp,
      costValue: DEFAULT_PRICEBOOK_LIST_FILTERS.costValue,
    };
  }
  return next;
}

const TYPE_DOT_COLORS: Record<string, string> = {
  service: 'bg-blue-500',
  material: 'bg-gray-500',
  equipment: 'bg-amber-500',
  membership: 'bg-green-500',
  fee: 'bg-purple-500',
  archived: 'bg-muted-foreground/50',
};

export const PRICEBOOK_SORT_OPTION_COUNT = 6;

const SORT_OPTIONS: SortOption[] = [
  { label: 'Created', sortBy: 'createdAt', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Created', sortBy: 'createdAt', sortDir: 'asc', icon: <ArrowUp size={12} /> },
  { label: 'Updated', sortBy: 'updatedAt', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Name', sortBy: 'name', sortDir: 'asc', icon: <ArrowUp size={12} /> },
  { label: 'Price', sortBy: 'price', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Price', sortBy: 'price', sortDir: 'asc', icon: <ArrowUp size={12} /> },
];

function matchesDateRange(value: string | undefined, range: DateRange | undefined): boolean {
  if (!range?.from && !range?.to) return true;
  if (!value) return false;

  const date = new Date(value);
  const time = date.getTime();
  if (Number.isNaN(time)) return false;

  if (range.from && time < range.from.getTime()) return false;
  if (range.to && time > endOfDay(range.to).getTime()) return false;
  return true;
}

function matchesNumberFilter(
  value: number | undefined,
  op: DataTableNumberOperator,
  raw: string,
): boolean {
  if (!raw.trim()) return true;
  const expected = Number(raw);
  if (!Number.isFinite(expected)) return true;
  const current = Number(value ?? 0);
  if (op === 'eq') return current === expected;
  if (op === 'gt') return current > expected;
  if (op === 'gte') return current >= expected;
  if (op === 'lt') return current < expected;
  return current <= expected;
}

function includesText(value: string | null | undefined, query: string): boolean {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  return String(value ?? '')
    .toLowerCase()
    .includes(normalized);
}

function dateRangeOf(from: string | undefined, to: string | undefined): DateRange | undefined {
  if (!from && !to) return undefined;
  return { from: from ? parseISO(from) : undefined, to: to ? parseISO(to) : undefined };
}

/**
 * The rows this panel shows, for the client-side filters. Exported so the cost
 * rule can be tested without a router, a query client and a rendered sheet.
 *
 * `costOf` is what a SERVICE actually costs — labor rows plus parts, the
 * figure the detail page and the table state — never the row's `costBasis`
 * scalar, which only the dynamic apply-price path ever writes.
 */
export function filterItems(
  items: PricebookItem[],
  filters: PricebookListFilters,
  options: {
    costOf: (item: PricebookItem) => number | undefined;
    hasBusinessUnits: boolean;
  },
): PricebookItem[] {
  const dateRange = dateRangeOf(filters.dateFrom, filters.dateTo);
  const updatedRange = dateRangeOf(filters.updatedFrom, filters.updatedTo);
  return items.filter((item) => {
    if (!matchesDateRange(item.createdAt, dateRange)) return false;
    if (!matchesDateRange(item.updatedAt, updatedRange)) return false;
    if (!includesText(item.sku, filters.sku)) return false;
    if (!matchesNumberFilter(pricebookPrimaryPrice(item), filters.priceOp, filters.priceValue))
      return false;
    // An UNKNOWN cost (labor profiles still loading, or profitability masked)
    // matches no cost filter — the same rule the table applies. Reading it as
    // $0 would let "= 0" list rows whose cost is simply not known yet.
    if (filters.costValue.trim()) {
      const cost = options.costOf(item);
      if (cost === undefined || !matchesNumberFilter(cost, filters.costOp, filters.costValue))
        return false;
    }
    if (filters.priceType !== 'all' && item.priceType !== filters.priceType) return false;
    if (
      options.hasBusinessUnits &&
      filters.businessUnit &&
      !(item.businessUnitIds ?? []).some((id) => includesText(id, filters.businessUnit))
    )
      return false;
    if (!includesText(item.unit, filters.unit)) return false;
    if (filters.taxable === 'yes' && !item.taxable) return false;
    if (filters.taxable === 'no' && item.taxable) return false;
    if (!includesText(item.glAccount, filters.glAccount)) return false;
    return true;
  });
}

function sortItems(items: PricebookItem[], option: SortOption): PricebookItem[] {
  return [...items].sort((a, b) => {
    const left = option.sortBy === 'price' ? pricebookPrimaryPrice(a) : (a[option.sortBy] ?? '');
    const right = option.sortBy === 'price' ? pricebookPrimaryPrice(b) : (b[option.sortBy] ?? '');
    const comparison =
      typeof left === 'number' && typeof right === 'number'
        ? left - right
        : String(left).localeCompare(String(right));
    return option.sortDir === 'asc' ? comparison : -comparison;
  });
}

export function PricebookListPanel({
  selectedId,
  onSelect,
  filters,
  onFiltersChange,
}: PricebookListPanelProps) {
  const {
    searchInput: search,
    setSearchInput: setSearch,
    debouncedSearch,
  } = useListPanelSearch({
    value: filters.q,
    onChange: (value) => onFiltersChange({ q: value }),
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const activeSort = SORT_OPTIONS[filters.sort] ?? SORT_OPTIONS[0];
  const dateRange = dateRangeOf(filters.dateFrom, filters.dateTo);
  const updatedRange = dateRangeOf(filters.updatedFrom, filters.updatedTo);

  const tagsQuery = useTags('pricebook_item');
  const { data: businessUnits = [] } = useBusinessUnits('active');
  const hasBusinessUnits = businessUnits.length > 0;
  const { data: categories = [] } = useCategoryList();
  const categoryPaths = buildCategoryPaths(categories);
  const canSeeArchived = useHasAnyPermission(...PRICEBOOK_ARCHIVED_FILTER_PERMISSIONS);
  const profitabilityHidden = useIsFieldHidden('PROFITABILITY');
  // A SERVICE's cost is composed from its labor rows and its parts, exactly as
  // the table column composes it — the row's `costBasis` is only ever written
  // by the dynamic apply-price path, so filtering on it silently dropped every
  // flat service. Not even fetched with profitability masked: the rates would
  // otherwise sit in the network payload beside the labor rows, one layer under
  // a filter the masked user is not offered.
  const laborRateProfilesQuery = useLaborRateProfiles({ enabled: !profitabilityHidden });
  const laborRateProfiles: LaborRateProfile[] | null = laborRateProfilesQuery.data ?? null;
  // Masked ⇒ the cost is UNKNOWN and matches no filter, so the filter cannot
  // become the side channel the hidden column is not.
  const costOf = (item: PricebookItem): number | undefined =>
    profitabilityHidden ? undefined : pricebookListCost(item, laborRateProfiles);
  const availableTags = tagsQuery.data ?? [];
  const visibleTags = availableTags.filter((t) =>
    t.name.toLowerCase().includes(tagSearch.toLowerCase()),
  );

  const itemsQuery = usePricebookItems({
    search: debouncedSearch || undefined,
    ...pricebookListParamsForTypeFilter(filters.type),
    tagIds: filters.tagIds.length > 0 ? filters.tagIds : undefined,
    // Server-side: only the backend can expand a folder to its subfolders, and
    // filtering the fetched page here would hide matches past the 100-row cap.
    categoryId: filters.categoryId || undefined,
    limit: 100,
  });

  const items = sortItems(
    filterItems(itemsQuery.data?.items ?? [], filters, { costOf, hasBusinessUnits }),
    activeSort,
  );
  const dynamicPreviews = useDynamicPricePreviews(
    items
      .filter((item) => item.type === 'service' && item.priceStrategy === 'DYNAMIC')
      .map((item) => item.id),
  );

  function clearDate() {
    onFiltersChange({ dateFrom: undefined, dateTo: undefined });
  }

  function clearUpdatedDate() {
    onFiltersChange({ updatedFrom: undefined, updatedTo: undefined });
  }

  const activeAdvancedCount =
    (filters.type !== 'all' ? 1 : 0) +
    (filters.sku.trim() ? 1 : 0) +
    (filters.priceValue.trim() ? 1 : 0) +
    (filters.costValue.trim() ? 1 : 0) +
    (filters.priceType !== 'all' ? 1 : 0) +
    (hasBusinessUnits && filters.businessUnit.trim() ? 1 : 0) +
    (filters.categoryId ? 1 : 0) +
    (filters.unit.trim() ? 1 : 0) +
    (filters.taxable !== 'all' ? 1 : 0) +
    (filters.glAccount.trim() ? 1 : 0) +
    (dateRange?.from || dateRange?.to ? 1 : 0) +
    (updatedRange?.from || updatedRange?.to ? 1 : 0) +
    filters.tagIds.length;

  function resetAdvancedFilters() {
    onFiltersChange({
      type: 'all',
      sku: '',
      priceOp: 'eq',
      priceValue: '',
      costOp: 'eq',
      costValue: '',
      priceType: 'all',
      businessUnit: '',
      categoryId: '',
      unit: '',
      taxable: 'all',
      glAccount: '',
      dateFrom: undefined,
      dateTo: undefined,
      updatedFrom: undefined,
      updatedTo: undefined,
      tagIds: [],
    });
    setTagSearch('');
  }

  return (
    <EntitySplitListPanel
      search={{
        value: search,
        onChange: setSearch,
        placeholder: 'Search items…',
      }}
      statusFilter={{
        value: filters.type,
        options: pricebookTypeFilterOptions(canSeeArchived),
        dotColors: TYPE_DOT_COLORS,
        onChange: (value) => onFiltersChange({ type: value as PricebookTypeFilter }),
      }}
      sort={{
        activeIndex: filters.sort,
        activeLabel: activeSort.label,
        options: SORT_OPTIONS,
        onSelect: (sort) => onFiltersChange({ sort }),
        onReset: () => onFiltersChange({ sort: 0 }),
      }}
      toolbarEnd={
        <EntityListAdvancedFiltersSheet
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          activeCount={activeAdvancedCount}
          onReset={resetAdvancedFilters}
          resetDisabled={activeAdvancedCount === 0}
        >
          <DataTableFilterSection
            label="Name"
            active={!!filters.q.trim()}
            onClear={() => onFiltersChange({ q: '' })}
          >
            <DataTableSearchFilterField
              value={filters.q}
              onValueChange={(value) => onFiltersChange({ q: value })}
              placeholder="Search by name…"
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="SKU / Code"
            active={!!filters.sku.trim()}
            onClear={() => onFiltersChange({ sku: '' })}
          >
            <DataTableTextFilterField
              value={filters.sku}
              onValueChange={(value) => onFiltersChange({ sku: value })}
              placeholder="e.g. SVC-001"
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Type"
            active={filters.type !== 'all'}
            onClear={() => onFiltersChange({ type: 'all' })}
          >
            <DataTableSelectFilterField
              value={filters.type}
              onValueChange={(value) => onFiltersChange({ type: value as PricebookTypeFilter })}
              placeholder="All types"
              options={pricebookTypeFilterOptions(canSeeArchived).map((filter) => ({
                value: filter.value,
                label: filter.value === 'all' ? 'All types' : filter.label,
              }))}
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Price"
            active={!!filters.priceValue.trim()}
            onClear={() => onFiltersChange({ priceOp: 'eq', priceValue: '' })}
          >
            <DataTableNumberFilterField
              op={filters.priceOp}
              value={filters.priceValue}
              onOpChange={(op) => onFiltersChange({ priceOp: op })}
              onValueChange={(value) => onFiltersChange({ priceValue: value })}
            />
          </DataTableFilterSection>

          {profitabilityHidden ? null : (
            <DataTableFilterSection
              label="Cost"
              active={!!filters.costValue.trim()}
              onClear={() => onFiltersChange({ costOp: 'eq', costValue: '' })}
            >
              <DataTableNumberFilterField
                op={filters.costOp}
                value={filters.costValue}
                onOpChange={(op) => onFiltersChange({ costOp: op })}
                onValueChange={(value) => onFiltersChange({ costValue: value })}
              />
            </DataTableFilterSection>
          )}

          <DataTableFilterSection
            label="Price Type"
            active={filters.priceType !== 'all'}
            onClear={() => onFiltersChange({ priceType: 'all' })}
          >
            <DataTableSelectFilterField
              value={filters.priceType}
              onValueChange={(value) =>
                onFiltersChange({ priceType: value as PricebookListFilters['priceType'] })
              }
              placeholder="All price types"
              options={[
                { value: 'all', label: 'All price types' },
                { value: 'flat', label: 'Flat' },
                { value: 'hourly', label: 'Hourly' },
              ]}
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Taxable"
            active={filters.taxable !== 'all'}
            onClear={() => onFiltersChange({ taxable: 'all' })}
          >
            <DataTableSelectFilterField
              value={filters.taxable}
              onValueChange={(value) =>
                onFiltersChange({ taxable: value as PricebookListFilters['taxable'] })
              }
              placeholder="All"
              options={[
                { value: 'all', label: 'All' },
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
              ]}
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Category"
            active={!!filters.categoryId}
            onClear={() => onFiltersChange({ categoryId: '' })}
          >
            <DataTableSelectFilterField
              value={filters.categoryId || 'all'}
              onValueChange={(value) =>
                onFiltersChange({ categoryId: value === 'all' ? '' : value })
              }
              placeholder="All"
              options={[
                { value: 'all', label: 'All categories' },
                // Full path, not just indentation. One tree covers the whole
                // catalog, so the same leaf name legitimately sits under several
                // parents; indenting shows how deep a folder is but not WHICH
                // parent it hangs off, leaving two "Filters" indistinguishable.
                ...categories.map((category) => ({
                  value: category.id,
                  label: categoryPaths.get(category.id) ?? category.name,
                })),
              ]}
            />
            <p className="mt-1.5 text-xs text-muted-foreground">
              Includes items filed in subcategories.
            </p>
          </DataTableFilterSection>

          {hasBusinessUnits ? (
            <DataTableFilterSection
              label="Business Unit"
              active={!!filters.businessUnit.trim()}
              onClear={() => onFiltersChange({ businessUnit: '' })}
            >
              <DataTableTextFilterField
                value={filters.businessUnit}
                onValueChange={(value) => onFiltersChange({ businessUnit: value })}
                placeholder="Filter by business unit…"
              />
            </DataTableFilterSection>
          ) : null}

          <DataTableFilterSection
            label="Unit"
            active={!!filters.unit.trim()}
            onClear={() => onFiltersChange({ unit: '' })}
          >
            <DataTableTextFilterField
              value={filters.unit}
              onValueChange={(value) => onFiltersChange({ unit: value })}
              placeholder="e.g. hr, sqft…"
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="GL Account"
            active={!!filters.glAccount.trim()}
            onClear={() => onFiltersChange({ glAccount: '' })}
          >
            <DataTableTextFilterField
              value={filters.glAccount}
              onValueChange={(value) => onFiltersChange({ glAccount: value })}
              placeholder="Account code…"
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Created At"
            active={!!(dateRange?.from || dateRange?.to)}
            onClear={clearDate}
          >
            <DataTableDateRangeField
              range={dateRange}
              onSelect={(range) =>
                onFiltersChange({
                  dateFrom: range?.from ? format(range.from, 'yyyy-MM-dd') : undefined,
                  dateTo: range?.to ? format(range.to, 'yyyy-MM-dd') : undefined,
                })
              }
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Updated At"
            active={!!(updatedRange?.from || updatedRange?.to)}
            onClear={clearUpdatedDate}
          >
            <DataTableDateRangeField
              range={updatedRange}
              onSelect={(range) =>
                onFiltersChange({
                  updatedFrom: range?.from ? format(range.from, 'yyyy-MM-dd') : undefined,
                  updatedTo: range?.to ? format(range.to, 'yyyy-MM-dd') : undefined,
                })
              }
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Tags"
            active={filters.tagIds.length > 0}
            onClear={() => onFiltersChange({ tagIds: [] })}
          >
            <div className="space-y-3">
              <DataTableSearchFilterField
                value={tagSearch}
                onValueChange={setTagSearch}
                placeholder="Search tags…"
              />
              <div className="max-h-44 overflow-y-auto rounded border">
                {visibleTags.length > 0 ? (
                  visibleTags.map((tag) => {
                    const selected = filters.tagIds.includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted/70"
                        onClick={() =>
                          onFiltersChange({
                            tagIds: selected
                              ? filters.tagIds.filter((id) => id !== tag.id)
                              : [...filters.tagIds, tag.id],
                          })
                        }
                      >
                        <span>{tag.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {selected ? 'Selected' : ''}
                        </span>
                      </button>
                    );
                  })
                ) : (
                  <p className="px-3 py-2 text-sm text-muted-foreground">No tags found</p>
                )}
              </div>
            </div>
          </DataTableFilterSection>
        </EntityListAdvancedFiltersSheet>
      }
      belowToolbar={<EntityListActiveDatePill range={dateRange} onClear={clearDate} />}
      isLoading={itemsQuery.isLoading}
      isError={itemsQuery.isError}
      onRetry={() => void itemsQuery.refetch()}
      emptyIcon={Package}
      emptyTitle="No items found"
      hasItems={items.length > 0}
      footer={
        !itemsQuery.isLoading ? (
          <EntityListCountFooter count={items.length} singular="item" />
        ) : null
      }
    >
      {items.map((item) => (
        <EntityListCompactItem
          key={item.id}
          selected={selectedId === item.id}
          onClick={() => onSelect(item)}
          content={
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-sm font-medium">
                <span className="truncate">{item.name}</span>
                {dynamicPreviews.data?.[item.id]?.updateAvailable && (
                  <RefreshCw
                    size={12}
                    className="shrink-0 text-amber-600"
                    aria-label="Pricing update available"
                  />
                )}
              </p>
              {item.sku && (
                <p className="mt-0.5 text-xs font-mono text-muted-foreground">{item.sku}</p>
              )}
            </div>
          }
          actions={
            <>
              <div className="shrink-0 text-right">
                <p className="text-sm font-semibold">
                  {fmt(
                    dynamicPreviews.data?.[item.id]?.currentPrice ??
                      (item.priceStrategy === 'DYNAMIC' ? item.price : pricebookPrimaryPrice(item)),
                  )}
                  {!dynamicPreviews.data?.[item.id] && pricebookPrimaryPriceSuffix(item) && (
                    <span className="text-xs font-normal text-muted-foreground">
                      {pricebookPrimaryPriceSuffix(item)}
                    </span>
                  )}
                </p>
                <div className="mt-0.5">
                  <ItemTypeBadge type={item.type} dynamic={isTimeAndMaterialsPricebookItem(item)} />
                </div>
              </div>
              <EntityRowActions>
                <PricebookItemActions
                  id={item.id}
                  name={item.name}
                  variant="row"
                  isArchived={String(item.status ?? '').toUpperCase() === 'ARCHIVED'}
                />
              </EntityRowActions>
            </>
          }
        />
      ))}
    </EntitySplitListPanel>
  );
}
